import { AppInsightsBlock, DatabaseBlock, MetricSeries, PlatformBlock } from '../_types/platform-health';

/// Funciones puras para los indicadores del panel de salud. Viven aquí (y no en cada componente de
/// pestaña) porque tanto "Visión global" como "API"/"Base de datos" pintan los mismos indicadores a
/// través de platform-health-api-indicators y platform-health-database-indicators: si esta lógica
/// se copiara en cada componente, un cambio en un cálculo tendría que repetirse en dos sitios.

/// Máximo de una serie, para los indicadores. Devuelve null si la serie no vino.
export function maxOf(series: MetricSeries[] | undefined, name: string): number | null {
    const found = series?.find((s) => s.name === name);
    if (!found) return null;
    const values = found.points.map((p) => p.maximum).filter((v): v is number => v !== null);
    return values.length ? Math.max(...values) : null;
}

/// Suma de una serie, para el total de peticiones del periodo.
export function sumOf(series: MetricSeries[] | undefined, name: string): number | null {
    const found = series?.find((s) => s.name === name);
    if (!found) return null;
    const values = found.points.map((p) => p.total).filter((v): v is number => v !== null);
    return values.length ? values.reduce((a, b) => a + b, 0) : null;
}

/// Mínimo de una serie (campo 'maximum', igual que maxOf): para el rango de instancias.
export function minOf(series: MetricSeries[] | undefined, name: string): number | null {
    const found = series?.find((s) => s.name === name);
    if (!found) return null;
    const values = found.points.map((p) => p.maximum).filter((v): v is number => v !== null);
    return values.length ? Math.min(...values) : null;
}

/// Media de la serie en el periodo. Promedia el campo "average" de cada punto, no el "maximum" que
/// usan maxOf y minOf.
///
/// El promedio de las medias por intervalo es la media real del periodo porque Azure Monitor
/// devuelve una rejilla uniforme: todos los intervalos duran lo mismo y pesan igual. Con una rejilla
/// irregular habría que ponderar por duración y esto dejaría de ser cierto.
export function avgOf(series: MetricSeries[] | undefined, name: string): number | null {
    const found = series?.find((s) => s.name === name);
    if (!found) return null;
    const values = found.points.map((p) => p.average).filter((v): v is number => v !== null);
    return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
}

/// Máximo del campo 'total' de una serie: para "Requests" cada punto trae el recuento de peticiones
/// de su intervalo en 'total' (igual que sumOf, que suma ese mismo campo), así que el intervalo más
/// cargado es el mayor de esos totales. Solo la usa peakRequestsPerSecond.
function maxTotalOf(series: MetricSeries[] | undefined, name: string): number | null {
    const found = series?.find((s) => s.name === name);
    if (!found) return null;
    const values = found.points.map((p) => p.total).filter((v): v is number => v !== null);
    return values.length ? Math.max(...values) : null;
}

/// Parsea una cadena de TimeSpan de .NET ("00:15:00", "1.00:00:00", "00:00:30.5000000") a segundos.
/// Devuelve null ante cualquier formato inesperado o duración negativa: mejor no pintar el
/// indicador que dividir por un número inventado.
function parseGranularitySeconds(value: string | null | undefined): number | null {
    if (!value) return null;
    const match = /^(-)?(?:(\d+)\.)?(\d{1,2}):(\d{1,2}):(\d{1,2})(?:\.(\d{1,7}))?$/.exec(value.trim());
    if (!match) return null;

    const [, neg, days, hours, minutes, seconds, fraction] = match;
    if (neg) return null;

    const totalSeconds =
        (days ? parseInt(days, 10) : 0) * 86400 +
        parseInt(hours, 10) * 3600 +
        parseInt(minutes, 10) * 60 +
        parseInt(seconds, 10) +
        (fraction ? parseInt(fraction.padEnd(7, '0'), 10) / 1e7 : 0);

    return totalSeconds > 0 ? totalSeconds : null;
}

/// Pico de peticiones por segundo: el intervalo más cargado (máximo de 'Requests' en total)
/// dividido entre la duración en segundos de ese intervalo (platform.granularity). Null si la
/// granularity no se puede parsear o no hay serie: no se enseña un número falso.
export function peakRequestsPerSecond(platform: PlatformBlock | null | undefined): number | null {
    const intervalSeconds = parseGranularitySeconds(platform?.granularity);
    if (!intervalSeconds) return null;
    const peak = maxTotalOf(platform?.series, 'Requests');
    if (peak === null) return null;
    return peak / intervalSeconds;
}

/// Porcentaje de 5xx del periodo: suma de Http5xx entre suma de Requests. Null si no hay
/// peticiones (para no dividir por cero) o si falta alguna de las dos series.
export function errorRatePercent(platform: PlatformBlock | null | undefined): number | null {
    const totalRequests = sumOf(platform?.series, 'Requests');
    const totalErrors = sumOf(platform?.series, 'Http5xx');
    if (!totalRequests || totalErrors === null) return null;
    return (totalErrors / totalRequests) * 100;
}

/// El peor p95 del periodo: el máximo de los p95 de Application Insights, punto a punto. No es
/// "el p95 del periodo" (promediar percentiles no es válido); es el intervalo en el que peor se
/// portó la latencia. Null si el bloque de Application Insights no respondió 'ok'.
export function worstP95Ms(appInsights: AppInsightsBlock | null | undefined): number | null {
    if (appInsights?.status?.status !== 'ok') return null;
    const values = (appInsights.latency ?? [])
        .map((p) => p.p95)
        .filter((v): v is number => v !== null && v !== undefined);
    return values.length ? Math.max(...values) : null;
}

/// Título de la gráfica "Base de datos", con el nombre de la base. Se usa tanto en "Visión global"
/// como en la pestaña "Base de datos": una sola función evita que las dos plantillas construyan el
/// mismo texto por su cuenta.
export function databaseChartTitle(database: DatabaseBlock | null | undefined): string {
    return `Base de datos — ${database?.databaseName ?? ''}`;
}

/// Título de la gráfica "Pool elástico", con el nombre del pool. Misma razón que databaseChartTitle.
export function poolChartTitle(database: DatabaseBlock | null | undefined): string {
    return `Pool elástico — ${database?.poolName ?? ''}`;
}

/// Motivo que se enseña cuando el inquilino SÍ tiene pool elástico pero ese periodo no trajo puntos
/// para su gráfica: un título sin gráfica debajo se leería como "el pool está tranquilo".
export function poolEmptyMessage(database: DatabaseBlock | null | undefined): string {
    return `Pool elástico — ${database?.poolName ?? ''}: no hay datos del pool para este periodo.`;
}

/// Motivo que se enseña cuando el bloque de base de datos llegó 'ok' (así que el general de arriba
/// no dice nada) pero buildChart() no encontró ninguna de las series de la gráfica "Base de datos"
/// con algún valor real para este periodo: mismo caso que poolEmptyMessage, para la gráfica principal.
export function databaseChartEmptyMessage(database: DatabaseBlock | null | undefined): string {
    return `${databaseChartTitle(database)}: no hay datos de esta métrica para este periodo.`;
}

/// Duración legible a partir de milisegundos transcurridos, para el diálogo de confirmación de
/// "cortar sesión": quien confirma tiene que poder leer "12 min" en lugar de "720000".
export function formatElapsedMs(elapsedMs: number): string {
    const totalSeconds = Math.floor(elapsedMs / 1000);
    if (totalSeconds < 60) return `${totalSeconds} s`;

    const totalMinutes = Math.floor(totalSeconds / 60);
    if (totalMinutes < 60) return `${totalMinutes} min`;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours} h ${minutes} min`;
}
